import { useState, ChangeEvent } from 'react';

import { AlertColor } from '@mui/material/Alert';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { assignJobsByProject, unassignJobsByProject } from 'src/api/jobs';
import { Flexbox } from 'src/components/Alignments';
import StyledRangeSlider from 'src/components/RangeSlider';
import useAlertSnackbar from 'src/hooks/useAlertSnackbar';
import useCheckedParams from 'src/hooks/useCheckedParams';
import { Modality } from 'src/types/api/data/image';

import CommonDialog, { CommonDialogProps } from './CommonDialog';

const JobAssignSliderContent = styled('div')`
  width: 80%;
`;

export const ASSIGNMENT_MODE = {
  ASSIGN: 'assign',
  UNASSIGN: 'unassign',
} as const;

export type AssignmentMode =
  (typeof ASSIGNMENT_MODE)[keyof typeof ASSIGNMENT_MODE];

export interface JobAssignDialogProps extends CommonDialogProps {
  mode?: AssignmentMode;
  assignableJobCount: number;
  annotatorName?: string;
  annotatorId?: string;
  projectId: string;
  onUpdate: () => void;
}

export default function JobAssignDialog({
  mode = ASSIGNMENT_MODE.ASSIGN,
  assignableJobCount,
  annotatorName = '',
  annotatorId = '',
  projectId,
  onUpdate,
  onClose,
  open,
}: JobAssignDialogProps): JSX.Element {
  const [jobCount, setJobCount] = useState(0);
  const { openAlertSnackbar } = useAlertSnackbar();
  const { modalityLabel } = useCheckedParams<{
    modalityLabel: Modality;
  }>(['modalityLabel']);

  const handleChangeRangeSlider = (value: number) => {
    setJobCount(value);
  };

  const handleChangeJobAssignCountInput = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let userValue = parseInt(e.target.value, 10);
    userValue =
      Number.isNaN(userValue) || !Number.isInteger(userValue) ? 0 : userValue;
    if (assignableJobCount < userValue) userValue = assignableJobCount;
    setJobCount(userValue);
  };

  const requestAssignJobs = async () => {
    try {
      const isAssignMode = mode === ASSIGNMENT_MODE.ASSIGN;
      const resp = isAssignMode
        ? await assignJobsByProject({
            projectId,
            userId: annotatorId,
            count: jobCount,
            modality: modalityLabel,
          })
        : await unassignJobsByProject({
            projectId,
            userId: annotatorId,
            count: jobCount,
            modality: modalityLabel,
          });

      const successCount =
        (resp as { success?: string[] }).success?.length ?? 0;
      const failCount = (resp as { failure?: string[] }).failure?.length ?? 0;
      let severity: AlertColor = 'success';
      let description = isAssignMode
        ? 'Successfully assigned new jobs'
        : 'Successfully unassigned jobs';

      if (successCount === 0 && failCount <= jobCount) {
        severity = 'error';
        description = isAssignMode
          ? 'Assigning jobs was unsuccessful. Please try again!'
          : 'Unassigning jobs was unsuccessful. Please try again!';
      }
      if (successCount > 0 && successCount < jobCount) {
        severity = 'warning';
        description = isAssignMode
          ? `Successfully assigned ${successCount} new jobs (but ${failCount} failed)`
          : `Successfully unassigned ${successCount} jobs (but ${failCount} failed)`;
      }

      openAlertSnackbar({ severity, description });
    } catch (e) {
      openAlertSnackbar({
        severity: 'error',
        description:
          mode === ASSIGNMENT_MODE.ASSIGN
            ? 'Failed to assign new jobs'
            : 'Failed to unassign jobs',
      });
    }
    onUpdate();
  };

  return (
    <CommonDialog
      onClose={onClose}
      title={mode === ASSIGNMENT_MODE.ASSIGN ? 'Assign Jobs' : 'Unassign Jobs'}
      open={open}
      fullWidth
    >
      <DialogContent>
        <Typography>
          <strong>Annotator:</strong> {annotatorName} [ID: {annotatorId}]
        </Typography>

        <Typography>
          <strong>
            {mode === ASSIGNMENT_MODE.ASSIGN
              ? 'Assignable Job Count'
              : 'Unassignable Job Count'}
            :
          </strong>{' '}
          {assignableJobCount}
        </Typography>

        <Typography>
          <strong>
            Job Count To{' '}
            {mode === ASSIGNMENT_MODE.ASSIGN ? 'Assign' : 'Unassign'}:
          </strong>{' '}
        </Typography>

        <Flexbox $justify="space-between">
          <JobAssignSliderContent>
            <StyledRangeSlider
              disabled={assignableJobCount === 0}
              max={assignableJobCount === 0 ? 1 : assignableJobCount}
              onChange={handleChangeRangeSlider}
              value={jobCount}
            />
          </JobAssignSliderContent>
          <TextField
            variant="outlined"
            size="small"
            color="primary"
            value={jobCount}
            onChange={handleChangeJobAssignCountInput}
            style={{ width: 80 }}
          />
        </Flexbox>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={requestAssignJobs}>
          {mode === ASSIGNMENT_MODE.ASSIGN ? 'Assign' : 'Unassign'}
        </Button>
      </DialogActions>
    </CommonDialog>
  );
}
